import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";

const TONE = {
  primary: {
    accent: (t) => t.palette.primary.main,
    soft: (t) => alpha(t.palette.primary.main, 0.12),
  },
  secondary: {
    accent: (t) => t.palette.secondary.main,
    soft: (t) => alpha(t.palette.secondary.main, 0.14),
  },
  info: {
    accent: (t) => t.palette.info.main,
    soft: (t) => alpha(t.palette.info.main, 0.14),
  },
  warning: {
    accent: (t) => t.palette.warning.dark,
    soft: (t) => alpha(t.palette.warning.main, 0.16),
  },
  error: {
    accent: (t) => t.palette.error.main,
    soft: (t) => alpha(t.palette.error.main, 0.12),
  },
};

/**
 * Confirmación MUI para acciones de workflow en fichas skill
 * (sustituye window.confirm del navegador).
 */
export default function SkillConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  eyebrow = "Skills · confirmación",
  confirmLabel = "Aceptar",
  cancelLabel = "Cancelar",
  icon: Icon = CheckCircleOutlineIcon,
  confirming = false,
  tone = "primary",
  confirmColor = "primary",
}) {
  const theme = useTheme();
  const palette = TONE[tone] || TONE.primary;
  const accent = palette.accent(theme);
  const paperBg =
    theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff";

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      BackdropProps={{
        sx: {
          bgcolor: alpha("#0f172a", 0.45),
          backdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: alpha(accent, 0.22),
          bgcolor: paperBg,
          backgroundImage: "none",
          opacity: 1,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 18px 50px rgba(0,0,0,0.55)"
              : `0 20px 56px ${alpha(accent, 0.16)}, 0 8px 24px rgba(15, 23, 42, 0.1)`,
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${accent}, ${alpha(
            theme.palette.secondary?.main || theme.palette.primary.light,
            0.85,
          )})`,
        }}
      />

      <DialogContent
        sx={{
          px: 3,
          pt: 2.75,
          pb: 1.5,
          bgcolor: paperBg,
          backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.07)} 0%, ${paperBg} 48%)`,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.75} alignItems="flex-start">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: palette.soft(theme),
                color: accent,
                border: "1px solid",
                borderColor: alpha(accent, 0.2),
              }}
            >
              <Icon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.15 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: alpha(accent, 0.95),
                }}
              >
                {eyebrow}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                lineHeight={1.25}
                sx={{ mt: 0.35 }}
              >
                {title}
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.55 }}
          >
            {description}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.75,
          pt: 1.25,
          gap: 1,
          justifyContent: "flex-end",
          bgcolor: paperBg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={confirming}
          variant="outlined"
          color="inherit"
          sx={{
            borderRadius: 999,
            fontWeight: 600,
            px: 2,
            borderColor: alpha(theme.palette.text.primary, 0.18),
            bgcolor: paperBg,
            "&:hover": {
              borderColor: alpha(theme.palette.text.primary, 0.28),
              bgcolor: alpha(theme.palette.text.primary, 0.04),
            },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={confirming}
          variant="contained"
          color={confirmColor}
          sx={{
            borderRadius: 999,
            fontWeight: 700,
            px: 2.25,
            ...(confirmColor === "primary" ? pagePrimaryButtonSx : null),
            boxShadow: `0 8px 20px ${alpha(accent, 0.28)}`,
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
